import { Button, DatePicker, Empty, Form, Input, Modal, Pagination, Popconfirm, Space, Switch, Table, Tabs, Tag, message, notification } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment, { Moment } from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import UnifiedReceiveDrawer from "../../components/delivery/unified_receive_drawer";
import WarehouseSelectionDrawer from "../../components/delivery/warehouse_selection_drawer";
import { usePermissions } from "../../custom_hooks/permission/permission_hook";
import {
  WAREHOUSES,
  WarehouseDelivery,
  WarehouseDirection,
  deriveDirectionFromStatus,
  isP2P,
  matchesIncomingWarehouse,
  warehouseLabel,
} from "../../interfaces/warehouse.interface";
import {
  dispatchWarehouseDelivery,
  downloadLegDocs,
  getWarehouseDeliveries,
  receiveWarehouseDelivery,
  unifiedReceiveWarehouseDelivery,
} from "./warehouseApi";
import { getPODeliveryStatusLabel } from "../../helpers/misc_helper";

const VEHICLE_REGEX = /^[A-Z]{2}[0-9]{1,2}[A-Z]{0,3}[0-9]{4}$/;
const DRIVER_PHONE_REGEX = /^[6-9]\d{9}$/;

const { RangePicker } = DatePicker;
const PAGE_SIZE = 10;

const WarehouseManager = () => {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission("delivery.update");
  const [activeWarehouseId, setActiveWarehouseId] = useState<number>(WAREHOUSES[0].id);
  const [includePrevious, setIncludePrevious] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>("");
  const [activeSearch, setActiveSearch] = useState<string>("");
  const [dateRange, setDateRange] = useState<[Moment | null, Moment | null] | null>(null);

  const [incomingPage, setIncomingPage] = useState<number>(1);
  const [outgoingPage, setOutgoingPage] = useState<number>(1);

  const [incomingRows, setIncomingRows] = useState<WarehouseDelivery[]>([]);
  const [incomingCount, setIncomingCount] = useState<number>(0);
  const [incomingLoading, setIncomingLoading] = useState<boolean>(false);

  const [outgoingRows, setOutgoingRows] = useState<WarehouseDelivery[]>([]);
  const [outgoingCount, setOutgoingCount] = useState<number>(0);
  const [outgoingLoading, setOutgoingLoading] = useState<boolean>(false);

  const [actingId, setActingId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [p2pDrawerVisible, setP2pDrawerVisible] = useState(false);
  const [p2pTargetRow, setP2pTargetRow] = useState<WarehouseDelivery | null>(null);
  const [unifiedReceiveDrawerVisible, setUnifiedReceiveDrawerVisible] = useState(false);
  const [unifiedReceiveTargetRow, setUnifiedReceiveTargetRow] = useState<WarehouseDelivery | null>(null);

  const [dispatchModalRow, setDispatchModalRow] = useState<WarehouseDelivery | null>(null);
  const [dispatchVehicle, setDispatchVehicle] = useState<string>("");
  const [dispatchPhone, setDispatchPhone] = useState<string>("");
  const [dispatchSubmitting, setDispatchSubmitting] = useState<boolean>(false);

  const vehicleValid = useMemo(() => VEHICLE_REGEX.test(dispatchVehicle), [dispatchVehicle]);
  const phoneValid = useMemo(() => DRIVER_PHONE_REGEX.test(dispatchPhone), [dispatchPhone]);
  const dispatchCanSubmit = vehicleValid && phoneValid && !dispatchSubmitting;

  const lastSearchIncomingRef = useRef<string>("");
  const lastSearchOutgoingRef = useRef<string>("");

  const isSearching = activeSearch !== "";

  const fetchIncoming = useCallback(async () => {
    setIncomingLoading(true);
    const data = await getWarehouseDeliveries({
      warehouse_id: activeWarehouseId,
      direction: "incoming",
      search: isSearching ? activeSearch : undefined,
      include_previous: !isSearching ? includePrevious : undefined,
      dispatch_date__gte: dateRange?.[0]?.format("YYYY-MM-DD"),
      dispatch_date__lte: dateRange?.[1]?.format("YYYY-MM-DD"),
      page: incomingPage,
      page_size: PAGE_SIZE,
    });
    if (!data) {
      setIncomingLoading(false);
      setIncomingRows([]);
      setIncomingCount(0);
      return;
    }
    let results = data.results || [];

    if (isSearching) {
      if (results.length > 0 && incomingPage === 1 && lastSearchIncomingRef.current !== activeSearch) {
        const first = results[0];
        const target =
          WAREHOUSES.find((w) => w.id === first.leg_destination_warehouse)?.id ??
          WAREHOUSES.find((w) => w.id === first.leg_origin_warehouse)?.id ??
          activeWarehouseId;

        lastSearchIncomingRef.current = activeSearch;
        if (target !== activeWarehouseId) {
          setActiveWarehouseId(target);
          return;
        }
      }

      results = results.filter((r) => matchesIncomingWarehouse(r, activeWarehouseId));
      setIncomingCount(results.length);
    } else {
      setIncomingCount(data.count || 0);
    }
    setIncomingLoading(false);
    setIncomingRows(results);
  }, [activeWarehouseId, includePrevious, activeSearch, dateRange, incomingPage, isSearching]);

  const fetchOutgoing = useCallback(async () => {
    setOutgoingLoading(true);
    const data = await getWarehouseDeliveries({
      warehouse_id: activeWarehouseId,
      direction: "outgoing",
      search: isSearching ? activeSearch : undefined,
      include_previous: !isSearching ? includePrevious : undefined,
      dispatch_date__gte: dateRange?.[0]?.format("YYYY-MM-DD"),
      dispatch_date__lte: dateRange?.[1]?.format("YYYY-MM-DD"),
      page: outgoingPage,
      page_size: PAGE_SIZE,
    });
    if (!data) {
      setOutgoingLoading(false);
      setOutgoingRows([]);
      setOutgoingCount(0);
      return;
    }
    let results = data.results || [];

    if (isSearching) {
      if (results.length > 0 && outgoingPage === 1 && lastSearchOutgoingRef.current !== activeSearch) {
        const first = results[0];
        const target =
          WAREHOUSES.find((w) => w.id === first.leg_destination_warehouse)?.id ??
          WAREHOUSES.find((w) => w.id === first.leg_origin_warehouse)?.id ??
          activeWarehouseId;

        lastSearchOutgoingRef.current = activeSearch;
        if (target !== activeWarehouseId) {
          setActiveWarehouseId(target);
          return;
        }
      }

      results = results.filter((r) => r.leg_origin_warehouse === activeWarehouseId);
      setOutgoingCount(results.length);
    } else {
      setOutgoingCount(data.count || 0);
    }
    setOutgoingLoading(false);
    setOutgoingRows(results);
  }, [activeWarehouseId, includePrevious, activeSearch, dateRange, outgoingPage, isSearching]);

  useEffect(() => {
    fetchIncoming();
  }, [fetchIncoming]);

  useEffect(() => {
    fetchOutgoing();
  }, [fetchOutgoing]);

  // Reset pages when filters change
  useEffect(() => {
    setIncomingPage(1);
    setOutgoingPage(1);
  }, [activeSearch, dateRange, includePrevious, activeWarehouseId]);

  // Reset the auto-switch guard when search is cleared so the next search
  // (even if it's the same term) can switch warehouses again.
  useEffect(() => {
    if (activeSearch === "") {
      lastSearchIncomingRef.current = "";
      lastSearchOutgoingRef.current = "";
    }
  }, [activeSearch]);

  const handleReceive = async (row: WarehouseDelivery) => {
    if (!canManage) {
      message.error("You don't have permission to receive deliveries");
      return;
    }
    if (isP2P(row)) {
      setP2pTargetRow(row);
      setP2pDrawerVisible(true);
      return;
    }
    setActingId(row.id);
    try {
      const res = await receiveWarehouseDelivery(row.id);
      message.success(res?.message || "Received");
      await fetchIncoming();
      await fetchOutgoing();
    } catch (e: any) {
      const errMsg = e?.response?.data?.error || e?.message || "Receive failed";
      message.error(errMsg);
    } finally {
      setActingId(null);
    }
  };

  const handleP2PDrawerFinish = async ({ vw_id }: { vw_id: number }) => {
    if (!canManage) {
      message.error("You don't have permission to receive deliveries");
      return;
    }
    if (!p2pTargetRow) return;
    setP2pDrawerVisible(false);
    setActingId(p2pTargetRow.id);
    try {
      const res = await receiveWarehouseDelivery(p2pTargetRow.id, vw_id);
      message.success(res?.message || "P2P order converted successfully");
      await fetchIncoming();
      await fetchOutgoing();
    } catch (e: any) {
      const errMsg = e?.response?.data?.error || e?.message || "P2P conversion failed";
      message.error(errMsg);
    } finally {
      setActingId(null);
      setP2pTargetRow(null);
    }
  };

  const handleOpenDispatchModal = (row: WarehouseDelivery) => {
    setDispatchModalRow(row);
    setDispatchVehicle("");
    setDispatchPhone("");
  };

  const handleCloseDispatchModal = () => {
    if (dispatchSubmitting) return;
    setDispatchModalRow(null);
    setDispatchVehicle("");
    setDispatchPhone("");
  };

  const handleDispatchSubmit = async () => {
    if (!canManage) {
      message.error("You don't have permission to dispatch deliveries");
      return;
    }
    if (!dispatchModalRow || !vehicleValid || !phoneValid) return;
    setDispatchSubmitting(true);
    setActingId(dispatchModalRow.id);
    try {
      const res = await dispatchWarehouseDelivery(dispatchModalRow.id, dispatchVehicle, dispatchPhone);
      message.success(res?.message || "Dispatched");
      setDispatchModalRow(null);
      setDispatchVehicle("");
      setDispatchPhone("");
      await fetchIncoming();
      await fetchOutgoing();
    } catch (e: any) {
      const data = e?.response?.data;
      const errorCode = data?.error_code || "DISPATCH_FAILED";
      const errorMsg = data?.message || data?.error || e?.message || "Dispatch failed";
      notification.error({ message: errorCode, description: errorMsg });
    } finally {
      setDispatchSubmitting(false);
      setActingId(null);
    }
  };

  const handleDownload = async (row: WarehouseDelivery) => {
    setDownloadingId(row.id);
    try {
      const result = await downloadLegDocs(row.id);
      if (!result.ok) {
        notification.error({
          message: result.errorCode || "DOWNLOAD_FAILED",
          description: result.message || "Could not download docs",
        });
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const handleUnifiedReceiveClick = (row: WarehouseDelivery) => {
    setUnifiedReceiveTargetRow(row);
    setUnifiedReceiveDrawerVisible(true);
  };

  const handleUnifiedReceiveFinish = async ({ chosen_warehouse_id }: { chosen_warehouse_id: number }) => {
    if (!canManage) {
      message.error("You don't have permission to receive deliveries");
      return;
    }
    if (!unifiedReceiveTargetRow) return;
    setUnifiedReceiveDrawerVisible(false);
    setActingId(unifiedReceiveTargetRow.id);
    try {
      const res = await unifiedReceiveWarehouseDelivery(unifiedReceiveTargetRow.id, chosen_warehouse_id);
      message.success(res?.message || "Receive successful");
      await fetchIncoming();
      await fetchOutgoing();
    } catch (e: any) {
      const errMsg = e?.response?.data?.error || e?.message || "Receive failed";
      message.error(errMsg);
    } finally {
      setActingId(null);
      setUnifiedReceiveTargetRow(null);
    }
  };

  const getColumns = (direction: WarehouseDirection) => {
    const cols: any[] = [
      {
        title: "PO Number",
        dataIndex: "po_number",
        key: "po_number",
        render: (v: string, row: WarehouseDelivery) => (
          <span style={{ fontFamily: "monospace" }}>
            {v}
            {isP2P(row) && <Tag color="orange" style={{ marginLeft: 6 }}>P2P</Tag>}
          </span>
        ),
      },
      {
        title: "Trip Name",
        dataIndex: "trip_name",
        key: "trip_name",
        render: (v: string | null) => v || "-",
      },
    ];

    if (isSearching) {
      cols.push({
        title: "Warehouse",
        dataIndex: "leg_destination_warehouse",
        key: "leg_destination_warehouse",
        render: (v: number | null) => warehouseLabel(v),
      });
      cols.push({
        title: "Direction",
        key: "derived_direction",
        render: (_: unknown, row: WarehouseDelivery) => {
          const d = deriveDirectionFromStatus(row.leg_status);
          return <Tag color={d === "outgoing" ? "orange" : "blue"}>{d}</Tag>;
        },
      });
    }

    cols.push({
      title: "Assigned Driver",
      dataIndex: "assigned_driver",
      key: "assigned_driver",
      render: (v: string) =>
        v === "Unassigned" ? <Tag color="default">Unassigned</Tag> : <span>{v}</span>,
    });
    cols.push({
      title: "Leg Status",
      dataIndex: "leg_status",
      key: "leg_status",
      render: (v: string | null) => (v ? <Tag>{v}</Tag> : "-"),
    });
    cols.push({
      title: "PO Status",
      dataIndex: "po_delivery_status",
      key: "po_delivery_status",
      render: (v: string | null) => {
        if (!v) return "-";
        const label = getPODeliveryStatusLabel(v);
        return <Tag color="blue">{label || v}</Tag>;
      },
    });
    cols.push({
      title: "Committed Dispatch",
      dataIndex: "leg_commited_dispatch_time",
      key: "leg_commited_dispatch_time",
      render: (v: string | null) => (v ? moment(v).format("DD MMM, HH:mm") : "-"),
    });
    cols.push({
      title: "Actions",
      key: "actions",
      width: 320,
      render: (_: unknown, row: WarehouseDelivery) => {
        const showUnifiedReceive = row.is_search_hit && !isP2P(row) && (row.leg_count ?? 0) > 1;
        // Dispatch is rendered only on outgoing rows. The backend already
        // separates search results into the incoming vs outgoing tables based
        // on the active warehouse, so the column's `direction` parameter
        // (set by getColumns(direction)) is the truth for both default-list
        // and search-hit rows. Do NOT derive direction from leg_status here:
        // leg_status "dispatch_pending" looks "outgoing" to that helper but
        // an incoming-table row in dispatch_pending is just an inbound leg
        // about to dispatch from its origin — incoming from our perspective.
        const showDispatch = direction === "outgoing";

        const downloadBtn = (
          <Button
            icon={<DownloadOutlined />}
            size="small"
            onClick={() => handleDownload(row)}
            loading={downloadingId === row.id}
          >
            Download
          </Button>
        );

        const dispatchBtn = showDispatch ? (
          <Button
            type="primary"
            size="small"
            onClick={() => handleOpenDispatchModal(row)}
            loading={actingId === row.id && dispatchSubmitting}
          >
            Dispatch
          </Button>
        ) : null;

        let receiveBtn: JSX.Element | null = null;
        if (showUnifiedReceive) {
          receiveBtn = (
            <Button
              type="primary"
              size="small"
              onClick={() => handleUnifiedReceiveClick(row)}
              loading={actingId === row.id && !dispatchSubmitting}
            >
              Receive
            </Button>
          );
        } else if (direction === "incoming") {
          receiveBtn = (
            <Popconfirm
              title="Receive this delivery?"
              description="This will create a GRN in Zoho and clone the delivery for the next leg."
              onConfirm={() => handleReceive(row)}
              okText="Receive"
              cancelText="Cancel"
              disabled={!row.can_receive}
            >
              <Button
                type="primary"
                size="small"
                disabled={!row.can_receive}
                loading={actingId === row.id && !dispatchSubmitting}
              >
                Receive
              </Button>
            </Popconfirm>
          );
        }

        return (
          <Space size="small" direction="horizontal" wrap={false} style={{ flexWrap: "nowrap" }}>
            {downloadBtn}
            {dispatchBtn}
            {receiveBtn}
          </Space>
        );
      },
    });

    return cols;
  };

  const outerTabItems = WAREHOUSES.map((w) => ({
    key: String(w.id),
    label: w.label,
  }));

  const renderTableSection = (
    title: string,
    direction: WarehouseDirection,
    rows: WarehouseDelivery[],
    loading: boolean,
    count: number,
    page: number,
    setPage: (p: number) => void
  ) => (
    <section style={{ marginBottom: 32 }} data-section={direction}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h4 style={{ margin: 0 }}>{title}</h4>
      </div>
      <div
        style={{
          border: "1px solid #d9d9d9",
          borderRadius: 8,
          overflow: "hidden",
          backgroundColor: "#fff",
          boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
        }}
      >
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          columns={getColumns(direction)}
          pagination={false}
          scroll={{ y: 400 }}
          size="middle"
          locale={{
            emptyText: (
              <Empty
                description={
                  loading
                    ? "Loading..."
                    : `No ${direction} deliveries for this warehouse${
                        dateRange
                          ? ""
                          : includePrevious
                          ? ""
                          : " in today → tomorrow"
                      }`
                }
              />
            ),
          }}
        />
        <div className="d-flex justify-content-end p-2 border-top">
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={count}
            showSizeChanger={false}
            onChange={setPage}
            size="small"
          />
        </div>
      </div>
    </section>
  );

  return (
    <div className="mx-3 my-3 px-3 py-2 white-color-bg">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Warehouse Manager</h3>
        <Space wrap>
          <Input.Search
            placeholder="Search by PO number"
            value={searchInput}
            onChange={(e) => {
              const v = e.target.value;
              setSearchInput(v);
              if (v === "") setActiveSearch("");
            }}
            onSearch={(val) => setActiveSearch(val.trim())}
            allowClear
            style={{ width: 240 }}
          />
          <RangePicker
            value={dateRange as any}
            onChange={(vals) => setDateRange(vals as [Moment | null, Moment | null] | null)}
            format="YYYY-MM-DD"
            allowClear
            placeholder={["Dispatch From", "Dispatch To"]}
          />
          <span>Include Previous Days</span>
          <Switch
            checked={includePrevious}
            onChange={setIncludePrevious}
            disabled={!!dateRange}
          />
        </Space>
      </div>

      <Tabs
        activeKey={String(activeWarehouseId)}
        onChange={(k) => setActiveWarehouseId(Number(k))}
        items={outerTabItems}
        size="middle"
      />

      <div style={{ marginTop: 16 }}>
        {renderTableSection(
          "Outgoing POs",
          "outgoing",
          outgoingRows,
          outgoingLoading,
          outgoingCount,
          outgoingPage,
          setOutgoingPage
        )}
        {renderTableSection(
          "Incoming POs",
          "incoming",
          incomingRows,
          incomingLoading,
          incomingCount,
          incomingPage,
          setIncomingPage
        )}
      </div>

      <WarehouseSelectionDrawer
        visible={p2pDrawerVisible}
        onFinish={handleP2PDrawerFinish}
        onClose={() => { setP2pDrawerVisible(false); setP2pTargetRow(null); }}
      />
      <UnifiedReceiveDrawer
        visible={unifiedReceiveDrawerVisible}
        onFinish={handleUnifiedReceiveFinish}
        onClose={() => { setUnifiedReceiveDrawerVisible(false); setUnifiedReceiveTargetRow(null); }}
      />

      <Modal
        title="Dispatch leg"
        open={dispatchModalRow !== null}
        onCancel={handleCloseDispatchModal}
        onOk={handleDispatchSubmit}
        okText="Dispatch"
        okButtonProps={{ disabled: !dispatchCanSubmit, loading: dispatchSubmitting }}
        cancelButtonProps={{ disabled: dispatchSubmitting }}
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item
            label="Vehicle Number"
            required
            validateStatus={dispatchVehicle && !vehicleValid ? "error" : ""}
            help={
              dispatchVehicle && !vehicleValid
                ? "Format must match e.g. KA52A5457"
                : undefined
            }
          >
            <Input
              value={dispatchVehicle}
              onChange={(e) => setDispatchVehicle(e.target.value.toUpperCase())}
              placeholder="KA52A5457"
              maxLength={11}
            />
          </Form.Item>
          <Form.Item
            label="Driver Phone"
            required
            validateStatus={dispatchPhone && !phoneValid ? "error" : ""}
            help={
              dispatchPhone && !phoneValid
                ? "Must be a 10-digit Indian mobile (starts with 6-9)"
                : undefined
            }
          >
            <Input
              value={dispatchPhone}
              onChange={(e) => setDispatchPhone(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="9876543210"
              maxLength={10}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WarehouseManager;

